import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PublicSlimHeader from '../../Common/PublicSlimHeader';
import ProofAttachmentDropzone from '../../Common/ProofAttachmentDropzone';
import { uploadFilePublic } from '../../../services/fileApi';
import {
  downloadPublicDirectDocumentPdf,
  getPublicDirectPayment,
  initiatePublicDirectCardPayment,
  submitPublicDirectOfflinePayment,
} from '../../../services/eventApi';
import { triggerBlobDownload } from '../../../utils/eventProformaStaffDownloads';
import {
  money,
  EventPaymentInstructions,
  statusPillClass,
  formatPaymentDate,
  formatPaymentMethodLabel,
  PublicPaymentPageLoading,
  PublicPaymentPageError,
  publicPaymentPageShellClass,
  publicPaymentPageContainerClass,
  publicPaymentHeaderProps,
} from '../Common/eventPublicPaymentUi';

const OFFLINE_LABEL = { BANK_TRANSFER: 'Bank transfer / deposit', CHEQUE: 'Cheque', CASH: 'Cash deposit' };
const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50';

const PublicDirectPaymentPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const paycorpStatus = String(searchParams.get('paycorp') || '').toLowerCase();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [data, setData] = useState(null);

  const [mode, setMode] = useState('OFFLINE');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('BANK_TRANSFER');
  const [reference, setReference] = useState('');
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [submittedMsg, setSubmittedMsg] = useState('');
  const modeInitialised = useRef(false);
  const [proof, setProof] = useState(null); // optional bank slip / cheque copy
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await getPublicDirectPayment(token);
      const payload = res?.data?.data ?? null;
      setData(payload);
      setAmount((prev) => prev || String(payload?.amountDue || ''));
      // Pick the starting payment mode once; later reloads must not switch tabs under the customer.
      if (!modeInitialised.current) {
        modeInitialised.current = true;
        if (payload?.cardEnabled) setMode('CARD');
      }
    } catch (err) {
      setLoadError(err?.response?.data?.message || err?.message || 'Could not load the payment page.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const download = async (type, params = {}, label = type) => {
    try {
      const res = await downloadPublicDirectDocumentPdf(token, type, params);
      triggerBlobDownload(res.data, `${label}.pdf`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not download the document.');
    }
  };

  const payByCard = async () => {
    setBusy(true);
    try {
      const res = await initiatePublicDirectCardPayment(token, { amount: Number(amount) });
      const url = res?.data?.data?.paymentPageUrl;
      if (!url) throw new Error('The payment page could not be opened.');
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Could not start the card payment.');
      setBusy(false);
    }
  };

  const pickProof = async (file) => {
    if (!file) { setProof(null); return; }
    setUploading(true);
    try {
      const res = await uploadFilePublic(file, {
        category: 'EVENT_DIRECT_PAYMENT_RECEIPT',
        description: `Payment proof for invoice ${data?.documentNumber || ''}`.trim(),
        title: file.name,
      });
      const f = res?.data?.data || res?.data || {};
      setProof({ fileId: f.fileId || f._id || '', fileName: f.fileName || file.name, originalName: f.originalName || file.name, fileSize: f.fileSize || file.size, fileType: f.fileType || file.type });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submitOffline = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await submitPublicDirectOfflinePayment(token, { amount: Number(amount), method, reference, paymentDate, notes, proofDocuments: proof?.fileId ? [proof] : [] });
      setSubmittedMsg(res?.data?.message || 'Payment submitted.');
      setReference('');
      setNotes('');
      setProof(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not submit the payment.');
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) return <PublicPaymentPageLoading />;
  if (loadError && !data) return <PublicPaymentPageError message={loadError} />;
  if (!data) return null;

  const cur = data.currency;
  const m = (n) => `${cur} ${Number(n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const isPaid = data.paymentStatus === 'PAID';
  const cancelled = data.status === 'CANCELLED';
  const canPay = !isPaid && !cancelled && !data.linkExpired && data.paymentLinkStatus === 'ACTIVE';
  const hasPending = (data.pendingPayments || []).length > 0;
  const invoiceType = data.documents?.invoiceType;

  return (
    <div className={publicPaymentPageShellClass}>
      <div className={publicPaymentPageContainerClass}>
        <PublicSlimHeader title={`Invoice payment • ${data.documentNumber}`} {...publicPaymentHeaderProps} />

        {paycorpStatus === 'success' && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            Thank you. Your card payment was successful and a receipt has been issued below.
          </div>
        )}
        {paycorpStatus === 'failed' && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">The card payment was not approved. You have not been charged. Please try again.</div>
        )}
        {paycorpStatus === 'cancelled' && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">The card payment was cancelled.</div>
        )}

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-5">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Invoice information</h2>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
                <p className="text-gray-700"><span className="font-medium text-gray-900">Event:</span> {data.event?.name || '—'}{data.event?.eventYear ? ` ${data.event.eventYear}` : ''}</p>
                <p className="text-gray-700"><span className="font-medium text-gray-900">Billed to:</span> {data.customerName || '—'}</p>
                {data.documents?.proforma && <p className="text-gray-700"><span className="font-medium text-gray-900">Proforma:</span> {data.documents.proforma}</p>}
                {data.documents?.invoice && <p className="text-gray-700"><span className="font-medium text-gray-900">{invoiceType === 'TAX_INVOICE' ? 'Tax invoice' : 'Non-tax invoice'}:</span> {data.documents.invoice}</p>}
                {data.linkExpiresAt && !data.linkExpired && !isPaid && (
                  <p className="text-gray-700 sm:col-span-2"><span className="font-medium text-gray-900">This link is valid until:</span> {new Date(data.linkExpiresAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${statusPillClass(isPaid ? 'PAID' : data.paymentStatus === 'PARTIALLY_PAID' ? 'PENDING_APPROVAL' : 'NOT_PAID')}`}>
                {cancelled ? 'Cancelled' : isPaid ? 'Paid' : data.paymentStatus === 'PARTIALLY_PAID' ? 'Partially paid' : 'Payment due'}
              </span>
              {data.linkExpired && <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border bg-red-50 text-red-800 border-red-200">Link expired</span>}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {data.documents?.proforma && (
              <button type="button" onClick={() => download('proforma', {}, `Proforma_${data.documentNumber}`)} className="px-3 py-1.5 rounded-md border border-gray-300 bg-white text-gray-800 text-sm hover:bg-gray-50">Download proforma</button>
            )}
            {invoiceType === 'TAX_INVOICE' && (
              <button type="button" onClick={() => download('tax-invoice', {}, `TaxInvoice_${data.documents.invoice}`)} className="px-3 py-1.5 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-800 text-sm hover:bg-indigo-100">Download tax invoice ({data.documents.invoice})</button>
            )}
            {invoiceType === 'INVOICE' && (
              <button type="button" onClick={() => download('invoice', {}, `Invoice_${data.documents.invoice}`)} className="px-3 py-1.5 rounded-md border border-sky-200 bg-sky-50 text-sky-800 text-sm hover:bg-sky-100">Download non-tax invoice ({data.documents.invoice})</button>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 sm:px-5 py-3 border-b border-gray-200"><h2 className="text-base sm:text-lg font-semibold text-gray-900">Invoice summary</h2></div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Qty</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Rate</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.lineItems.map((l, i) => (
                  <tr key={`${l.description}-${i}`}>
                    <td className="px-4 py-2 text-gray-800">{l.description}{l.discountAmount ? <span className="ml-2 text-xs text-gray-500">(discount {m(l.discountAmount)})</span> : null}</td>
                    <td className="px-4 py-2 text-right text-gray-700">{l.quantity}</td>
                    <td className="px-4 py-2 text-right text-gray-700">{m(l.rate)}</td>
                    <td className="px-4 py-2 text-right text-gray-900">{m(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 sm:px-5 py-3 border-t border-gray-100 ml-auto max-w-sm text-sm space-y-1">
            <div className="flex justify-between"><span className="text-gray-500">Gross</span><span>{m(data.subTotal)}</span></div>
            {data.totalDiscount > 0 && <div className="flex justify-between"><span className="text-gray-500">Discount</span><span>− {m(data.totalDiscount)}</span></div>}
            {(data.taxLines || []).map((t) => <div key={t.taxCode || t.taxName} className="flex justify-between"><span className="text-gray-500">{t.taxName} {t.percentage}%</span><span>{m(t.amount)}</span></div>)}
            <div className="flex justify-between font-semibold border-t border-gray-200 pt-1"><span>Total</span><span>{m(data.grandTotal)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Paid</span><span>{m(data.amountPaid)}</span></div>
            <div className="flex justify-between font-semibold text-indigo-700"><span>Balance due</span><span>{m(data.amountDue)}</span></div>
          </div>
        </div>

        {(data.receipts || []).length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 sm:px-5 py-3 border-b border-gray-200"><h2 className="text-base sm:text-lg font-semibold text-gray-900">Payments received</h2></div>
            <ul className="divide-y divide-gray-100 text-sm">
              {data.receipts.map((r) => (
                <li key={r.paymentKey} className="px-4 sm:px-5 py-2.5 flex flex-wrap items-center justify-between gap-2">
                  <span><span className="font-mono text-gray-900">{r.receiptNumber}</span> <span className="text-gray-500">· {formatPaymentMethodLabel(r.method)} · {formatPaymentDate(r.generatedAt)}</span></span>
                  <span className="flex items-center gap-3"><span className="font-medium text-gray-900">{m(r.amount)}</span>
                    <button type="button" onClick={() => download('receipt', { paymentKey: r.paymentKey }, `Receipt_${r.receiptNumber}`)} className="text-indigo-600 hover:text-indigo-800 text-xs font-medium">Download receipt</button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {hasPending && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-semibold">Waiting for the organiser to confirm</p>
            <ul className="mt-1 list-disc pl-5">
              {data.pendingPayments.map((p, i) => <li key={i}>{m(p.amount)} by {formatPaymentMethodLabel(p.method)} (ref {p.reference})</li>)}
            </ul>
          </div>
        )}

        {isPaid && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">This invoice is fully paid. Thank you.</div>}
        {cancelled && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">This invoice has been cancelled. No payment is needed.</div>}
        {!isPaid && !cancelled && !canPay && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {data.linkExpired ? 'This payment link has expired. Please contact the organiser to renew it.' : 'This payment link is no longer active. Please contact the organiser.'}
          </div>
        )}

        {canPay && (
          <>
            <EventPaymentInstructions paymentDisplay={data.paymentDisplay} bankAccount={data.bankAccount} showCardSettlementNote={!!data.cardEnabled} />
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-5 space-y-4">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Make a payment</h2>
              {data.cardEnabled && (
                <div className="flex gap-2">
                  <button type="button" onClick={() => setMode('CARD')} className={`px-3 py-1.5 rounded-md border text-sm ${mode === 'CARD' ? 'border-indigo-500 bg-indigo-50 text-indigo-800' : 'border-gray-300 text-gray-700'}`}>Pay by card</button>
                  <button type="button" onClick={() => setMode('OFFLINE')} className={`px-3 py-1.5 rounded-md border text-sm ${mode === 'OFFLINE' ? 'border-indigo-500 bg-indigo-50 text-indigo-800' : 'border-gray-300 text-gray-700'}`}>Bank transfer / cheque / cash</button>
                </div>
              )}
              <div className="max-w-xs">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Amount ({cur}) — partial payments are welcome</label>
                <input className={inputClass} type="number" min="0" max={mode === 'CARD' && data.cardEnabled ? data.amountDue : undefined} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
                {mode !== 'CARD' && Number(amount) > data.amountDue + 0.005 && (
                  <p className="mt-1 text-xs text-amber-700">This is more than the balance due. The extra will be kept as credit on your account once the organiser confirms the payment.</p>
                )}
              </div>
              {mode === 'CARD' && data.cardEnabled ? (
                <button type="button" disabled={busy || !(Number(amount) > 0)} onClick={payByCard} className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50">
                  {busy ? 'Opening payment page…' : `Pay ${m(Number(amount) || 0)} by card`}
                </button>
              ) : (
                <form onSubmit={submitOffline} className="space-y-3">
                  <p className="text-xs text-gray-500">Pay using the instructions above, then tell us the details. Your payment is confirmed once the organiser has checked it.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Method</label>
                      <select className={inputClass} value={method} onChange={(e) => setMethod(e.target.value)}>
                        {(data.offlineMethods || []).map((k) => <option key={k} value={k}>{OFFLINE_LABEL[k] || k}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Reference (slip / cheque no.) *</label>
                      <input className={inputClass} value={reference} maxLength={120} onChange={(e) => setReference(e.target.value)} required />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Payment date *</label>
                      <input className={inputClass} type="date" max={new Date().toISOString().slice(0, 10)} value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} required />
                    </div>
                  </div>
                  <ProofAttachmentDropzone
                    label="Payment slip / proof"
                    hint="(optional, but it helps us confirm faster)"
                    file={proof}
                    busy={uploading}
                    onChange={pickProof}
                  />
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Notes</label>
                    <textarea className={inputClass} rows={2} maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} />
                  </div>
                  <button type="submit" disabled={busy || uploading || !(Number(amount) > 0) || !reference.trim()} className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50">
                    {busy ? 'Submitting…' : 'Submit payment details'}
                  </button>
                  {submittedMsg && <p className="text-sm text-emerald-700">{submittedMsg}</p>}
                </form>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicDirectPaymentPage;
